import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock } from 'lucide-react';
import { TeamSummaryView, TeamMemberLeaveView, LeaveTypeView } from '../../../../models/attendance';
import { getLeaveTypeBadgeStyle } from '../../../../utils/leaveTypeUtils';
import { getFullUrl } from '../../../../utils/url';
import { appDayMonth } from '../../../../utils/appDateFormat';
import { parseApiDateOnly } from '@/utils/apiDate';

interface TeamLeaveSummaryProps {
  teamSummary: TeamSummaryView | null;
  isLoading: boolean;
  leaveTypes?: LeaveTypeView[];
}

type ViewMode = 'current' | 'upcoming';

const TeamLeaveSummary: React.FC<TeamLeaveSummaryProps> = ({ teamSummary, isLoading, leaveTypes }) => {
  const { t } = useTranslation(['leaves', 'common', 'translation']);
  const [viewMode, setViewMode] = useState<ViewMode>('current');

  const formatDate = (dateString: string): string => {
    return appDayMonth(parseApiDateOnly(dateString));
  };

  const formatDateRange = (startDate: string, endDate: string): string => {
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    return start === end ? start : `${start} - ${end}`;
  };

  const getDaysUntil = (startDate: string): number => {
    const today = new Date();
    const start = (parseApiDateOnly(startDate) ?? new Date(NaN));
    return Math.ceil((start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getDaysRemaining = (endDate: string): number => {
    const today = new Date();
    const end = (parseApiDateOnly(endDate) ?? new Date(NaN));
    return Math.max(0, Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  };

  const coverage = teamSummary?.coveragePercentage ?? 100;
  const leaves: TeamMemberLeaveView[] = viewMode === 'current'
    ? (teamSummary?.currentLeaves || [])
    : (teamSummary?.upcomingLeavesList || []);

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-20 bg-muted/30 rounded-lg" />
        <div className="h-10 bg-muted/30 rounded-lg" />
        <div className="h-32 bg-muted/30 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-summary">
      {/* Coverage Metrics */}
      <div className="bg-muted/30 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-muted-foreground">{t('translation:teamCoverageToday', 'Team Coverage (Today)')}</span>
          <span className={`text-lg font-bold ${
            coverage >= 80 ? 'text-green-600' :
            coverage >= 60 ? 'text-orange-600' : 'text-red-600'
          }`}>
            {Math.round(coverage)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              coverage >= 80 ? 'bg-green-500' :
              coverage >= 60 ? 'bg-orange-500' : 'bg-red-500'
            }`}
            style={{ width: `${coverage}%` }}
          />
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          {teamSummary?.onLeaveToday || 0} {t('translation:currentlyOnLeave', 'currently on leave')} &bull; {teamSummary?.upcomingLeaves || 0} {t('translation:upcoming', 'upcoming')}
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center space-x-1 rtl:space-x-reverse bg-muted/30 rounded-lg p-1">
        <button
          onClick={() => setViewMode('current')}
          className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
            viewMode === 'current'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="leave-team-summary-current"
        >
          {t('translation:currentLeaves', 'Current Leaves')}
        </button>
        <button
          onClick={() => setViewMode('upcoming')}
          className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
            viewMode === 'upcoming'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="leave-team-summary-upcoming"
        >
          {t('translation:upcoming', 'Upcoming')}
        </button>
      </div>

      {/* Leaves List */}
      <div className="space-y-3">
        {leaves.length > 0 ? (
          leaves.map((leave, index) => (
            <div key={`${leave.userId}-${index}`} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                    {getFullUrl(leave.imageUrl) ? (
                      <img src={getFullUrl(leave.imageUrl)!} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs font-medium text-muted-foreground">
                        {leave.userName?.charAt(0) || '?'}
                      </span>
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground text-sm">{leave.userName}</h4>
                  </div>
                </div>
                <div
                  className={`px-2 py-1 rounded text-xs font-medium ${getLeaveTypeBadgeStyle(leave.leaveType, leaveTypes).colorClass}`}
                  style={getLeaveTypeBadgeStyle(leave.leaveType, leaveTypes).style}
                >
                  {leave.leaveType}
                </div>
              </div>
              <div className="mb-2">
                <p className="text-sm font-medium text-foreground">
                  {formatDateRange(leave.startDate, leave.endDate)}
                </p>
              </div>
              <div className="text-xs text-muted-foreground">
                {viewMode === 'current' ? (
                  <span>{getDaysRemaining(leave.endDate)} {t('translation:daysRemaining', 'days remaining')}</span>
                ) : (
                  <span>{t('translation:startsIn', 'Starts in')} {getDaysUntil(leave.startDate)} {t('translation:days', 'days')}</span>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-muted/30 rounded-xl flex items-center justify-center mx-auto mb-3">
              {viewMode === 'current' ? (
                <Calendar size={24} className="text-muted-foreground" />
              ) : (
                <Clock size={24} className="text-muted-foreground" />
              )}
            </div>
            <h4 className="font-medium text-foreground mb-1">
              {viewMode === 'current'
                ? t('translation:noCurrentLeaves', 'No current leaves')
                : t('translation:noUpcomingLeaves', 'No upcoming leaves')
              }
            </h4>
            <p className="text-sm text-muted-foreground">
              {viewMode === 'current'
                ? t('translation:noCurrentLeavesDesc', 'No team members are currently on leave')
                : t('translation:noUpcomingLeavesDesc', 'No upcoming leaves scheduled')
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamLeaveSummary;
